import { useState, useEffect, useRef } from 'react'
import { Upload, Trash2, QrCode } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { getPaymentQr, uploadPaymentQr, deletePaymentQr } from '@/services/settingsService'
import { BACKEND_ORIGIN } from '@/services/roomService'

export function PaymentQrSettings() {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    getPaymentQr()
      .then(setImageUrl)
      .catch(() => toast.error('Không thể tải QR thanh toán'))
      .finally(() => setLoading(false))
  }, [])

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    try {
      const url = await uploadPaymentQr(file)
      setImageUrl(url)
      toast.success('Đã cập nhật QR thanh toán')
    } catch {
      toast.error('Lỗi khi tải lên QR')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await deletePaymentQr()
      setImageUrl(null)
      toast.success('Đã xóa QR, đang dùng QR tự động')
    } catch {
      toast.error('Lỗi khi xóa QR')
    } finally {
      setDeleting(false)
    }
  }

  const fullImageUrl = imageUrl
    ? imageUrl.startsWith('http') ? imageUrl : `${BACKEND_ORIGIN}${imageUrl}`
    : null

  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-foreground">QR Thanh Toán</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Upload ảnh QR tĩnh để hiển thị cho khách khi thanh toán.
      </p>

      <div className="mt-4">
        {loading ? (
          <div className="flex h-[200px] w-[200px] items-center justify-center rounded-lg border border-dashed border-muted-foreground/30">
            <span className="text-sm text-muted-foreground">Đang tải...</span>
          </div>
        ) : fullImageUrl ? (
          <div className="space-y-3">
            <img
              src={fullImageUrl}
              alt="QR thanh toán"
              className="h-[200px] w-[200px] rounded-lg border border-border object-contain"
            />
            <p className="text-xs text-muted-foreground truncate max-w-[300px]">
              {imageUrl}
            </p>
          </div>
        ) : (
          <div className="flex h-[200px] w-[200px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-muted-foreground/30">
            <QrCode className="h-8 w-8 text-muted-foreground/50" />
            <span className="text-sm text-muted-foreground text-center px-2">
              Đang dùng QR tự động (VietQR)
            </span>
          </div>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleUpload}
        />
        <Button
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Đang tải...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Tải lên QR mới
            </span>
          )}
        </Button>

        {imageUrl && (
          <Button
            variant="destructive"
            size="sm"
            disabled={deleting}
            onClick={handleDelete}
          >
            {deleting ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Đang xóa...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                Xóa QR tĩnh
              </span>
            )}
          </Button>
        )}
      </div>
    </div>
  )
}
